import React from "react"

const MedicalDecor = () => {
  return (
    <div className="relative h-full w-full overflow-hidden" aria-hidden="true">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-slate-50" />
      <div className="absolute inset-0">
        <svg
          className="h-full w-full text-gray-200"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <defs>
            <pattern
              id="automation-dots"
              x="0"
              y="0"
              width="24"
              height="24"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="1" cy="1" r="1" fill="currentColor" />
            </pattern>
            <radialGradient id="automation-halo" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.16" />
              <stop offset="60%" stopColor="#4f46e5" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#4f46e5" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#automation-dots)" />
        </svg>
      </div>

      <div className="absolute inset-0">
        <style>{`
          @keyframes floatY { 
            0%{ transform: translateY(0) } 
            50%{ transform: translateY(-10px) } 
            100%{ transform: translateY(0) } 
          }
          @keyframes slideLoop {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>

        {/* Top scrolling labels */}
        <div className="absolute top-6 left-0 right-0 hidden px-6 md:block">
          <div className="relative mx-auto max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white/90 shadow-lg">
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-white via-transparent to-white" />
            <div
              className="flex min-w-max gap-4 px-4 py-3 text-[11px]"
              style={{ animation: "slideLoop 26s linear infinite" }}
            >
              {[
                "Define the role once",
                "Let AI generate interview questions",
                "Share one link with candidates",
                "See every stage on one timeline",
                "Reduce manual screening work",
                "Move from interview to offer faster",
              ].map((text, i) => (
                <div
                  key={`${text}-${i}`}
                  className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-[10px] font-medium text-gray-700"
                >
                  {text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Horizontal hiring pipeline */}
        <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-center px-6">
          <div className="relative w-full max-w-xl">
            {/* network connectors in blue linking top, middle and bottom strips */}
            <div className="pointer-events-none absolute inset-x-0 top-0 bottom-0">
              <svg
                className="h-full w-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                {/* horizontal rails */}
                <line x1="8" y1="18" x2="92" y2="18" stroke="#2563eb" strokeWidth="0.6" strokeDasharray="2 3" opacity="0.4" />
                <line x1="8" y1="50" x2="92" y2="50" stroke="#2563eb" strokeWidth="0.7" opacity="0.55" />
                <line x1="8" y1="82" x2="92" y2="82" stroke="#2563eb" strokeWidth="0.6" strokeDasharray="2 3" opacity="0.35" />

                {/* vertical & diagonal links between rails */}
                {[15, 35, 65, 85].map((x, idx) => (
                  <g key={idx}>
                    {/* vertical stems */}
                    <line x1={x} y1="18" x2={x} y2="50" stroke="#2563eb" strokeWidth="0.6" strokeDasharray="1.2 2.4" opacity="0.7" />
                    <line x1={x} y1="50" x2={x} y2="82" stroke="#2563eb" strokeWidth="0.6" strokeDasharray="1.2 2.4" opacity="0.6" />
                    {/* small diagonals to suggest network branches */}
                    <line x1={x} y1="34" x2={x + 6} y2="46" stroke="#2563eb" strokeWidth="0.5" opacity="0.5" />
                    <line x1={x} y1="66" x2={x - 6} y2="54" stroke="#2563eb" strokeWidth="0.5" opacity="0.45" />

                    {/* nodes */}
                    <circle cx={x} cy="18" r="1.3" fill="#3b82f6" opacity="0.95" />
                    <circle cx={x} cy="50" r="1.6" fill="#1d4ed8" opacity="0.95" />
                    <circle cx={x} cy="82" r="1.3" fill="#3b82f6" opacity="0.9" />
                  </g>
                ))}
              </svg>
            </div>

            {/* subtle line behind cards */}
            <div className="absolute left-4 right-4 top-1/2 -translate-y-1/2">
              <div className="h-0.5 w-full rounded-full bg-gradient-to-r from-blue-200 via-blue-300 to-blue-200" />
            </div>

            <div className="relative grid grid-cols-4 gap-4">
              {/* Step 1: Job posted */}
              <div
                className="relative"
                style={{ animation: "floatY 8s ease-in-out infinite" }}
              >
                <div className="absolute -top-3 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-indigo-500 shadow shadow-indigo-300" />
                <div className="rounded-2xl border border-indigo-100 bg-white/95 px-3 py-3 shadow-sm">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[9px] font-semibold text-indigo-700">
                      Step 1
                    </span>
                    <span className="text-[9px] text-gray-400">Job</span>
                  </div>
                  <div className="text-[11px] font-semibold text-gray-900">
                    Job posted
                  </div>
                  <div className="mt-1 h-1.5 w-full rounded-full bg-gray-100">
                    <div className="h-1.5 w-1/3 rounded-full bg-indigo-500" />
                  </div>
                  <div className="mt-1 text-[9px] text-gray-500">
                    JD created with AI suggestions.
                  </div>
                </div>
              </div>

              {/* Step 2: Applicants screened */}
              <div
                className="relative"
                style={{ animation: "floatY 9s ease-in-out -1s infinite" }}
              >
                <div className="absolute -top-3 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-sky-500 shadow shadow-sky-300" />
                <div className="rounded-2xl border border-sky-100 bg-white/95 px-3 py-3 shadow-sm">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[9px] font-semibold text-sky-700">
                      Step 2
                    </span>
                    <span className="text-[9px] text-gray-400">Screen</span>
                  </div>
                  <div className="text-[11px] font-semibold text-gray-900">
                    Applicants screened
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-[9px] text-gray-600">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    <span>Best fit surfaced automatically.</span>
                  </div>
                </div>
              </div>

              {/* Step 3: Interviews & scheduling */}
              <div
                className="relative"
                style={{ animation: "floatY 8s ease-in-out -2s infinite" }}
              >
                <div className="absolute -top-3 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-amber-500 shadow shadow-amber-300" />
                <div className="rounded-2xl border border-amber-100 bg-white/95 px-3 py-3 shadow-sm">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[9px] font-semibold text-amber-700">
                      Step 3
                    </span>
                    <span className="text-[9px] text-gray-400">Interview</span>
                  </div>
                  <div className="text-[11px] font-semibold text-gray-900">
                    Interviews scheduled
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[9px] text-gray-600">
                    <span>Today • 3 slots</span>
                    <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[8px] font-semibold text-white">
                      Auto
                    </span>
                  </div>
                </div>
              </div>

              {/* Step 4: Offer & hire */}
              <div
                className="relative"
                style={{ animation: "floatY 9s ease-in-out -3s infinite" }}
              >
                <div className="absolute -top-3 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-emerald-500 shadow shadow-emerald-300" />
                <div className="rounded-2xl border border-emerald-200 bg-gray-900 px-3 py-3 text-white shadow-md">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[9px] font-semibold text-emerald-200">
                      Step 4
                    </span>
                    <span className="text-[9px] text-gray-300">Offer</span>
                  </div>
                  <div className="text-[11px] font-semibold">
                    Offer sent & hire
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[9px] text-gray-200">
                    <span>₦450,000 • Apr 2</span>
                    <span className="rounded-full bg-emerald-400/20 px-2 py-0.5 text-[8px] font-semibold text-emerald-200">
                      Completed
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom scrolling labels */}
        <div className="absolute bottom-6 left-0 right-0 hidden px-6 md:block">
          <div className="relative mx-auto max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white/90 shadow-lg">
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-white via-transparent to-white" />
            <div
              className="flex min-w-max gap-4 px-4 py-3 text-[11px]"
              style={{ animation: "slideLoop 26s linear infinite" }}
            >
              {[
                "Auto-reminders keep candidates on time",
                "Hiring managers review one simple view",
                "See which stage each candidate is in",
                "Share links with your wider team",
                "Keep interviews and offers auditable",
                "Close roles without losing the history",
              ].map((text, i) => (
                <div
                  key={`${text}-${i}`}
                  className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-[10px] font-medium text-gray-700"
                >
                  {text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MedicalDecor
